import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEdit,
  faSave,
  faRightToBracket,
  faChevronDown,
  faChevronRight,
  faTimes,
  faEye,
  faEyeSlash,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewHost,
  deleteHostById,
  getHostHTMLContent,
  tryAgainHostById,
  updateHostById,
} from "../services/hostService";
import { addHost, updateHost, deleteHost } from "../redux/slices/hostsSlice";
import HostPreview from "../components/HostPreview";
import { getTranslation } from "../translations/getTranslations";
import { useNotification } from "../notifications/Notifications";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { NoData } from "../components/NoData";
import { gethostStatuses } from "../services/statusService";
import { initializeHost } from "../redux/slices/hostsSlice";
import { getAllHosts } from "../services/hostService";
import Gateway from "../components/Gateway";

const Hosts = ({ isMobile, toggleNavbarCollapse, setIsToggleButtonHidden }) => {
  const [showDeviceForm, setShowDeviceForm] = useState(false);
  const [newDevice, setNewDevice] = useState({ name: "", url: "" });
  const [hostDetails, setHostDetails] = useState({ name: "", url: "", html: "" });
  const [showPanelTypeDropdown, setShowPanelTypeDropdown] = useState(false);
  const [showHttpTypeDropdown, setShowHttpTypeDropdown] = useState(false);
  const [showSelectedGateway, setShowSelectedGateway] = useState(false);
  const [selectedPanelType, setSelectedPanelType] = useState("BSR-1004");
  const [selectedHttpType, setSelectedHttpType] = useState("http://");
  const [editIndex, setEditIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProxyReloading, setIsProxyReloading] = useState(false);
  const [temporaryName, setTemporaryName] = useState("");
  const panelDropdownRef = useRef(null);
  const [phosUsername, setPhosUsername] = useState("");
  const [phosPassword, setPhosPassword] = useState("");
  const [showPasswordAddPhosDevice, setShowPasswordAddPhosDevice] = useState(false);

  const [usernamePhosCard, setUsernamePhosCard] = useState("");
  const [passwordPhosCard, setPasswordPhosCard] = useState("");
  const [showPasswordMap, setShowPasswordMap] = useState({});

  const dispatch = useDispatch();
  const notify = useNotification();
  const language = useSelector(state => state.settings.language);
  const devices = useSelector(state => state.hosts.data);
  const selectedGateway = useSelector(state => state.gateways.selectedGateway);
  const translation = useCallback(key => getTranslation("hosts", key, language), [language]);

  const httpTypes = ["http://", "https://"];
  let panelTypes = [];

  if (selectedGateway.apiVersion < "1.0.0.23") {
    panelTypes = ["BSR-1004", "BS-1638", "GR-6500", "GR-7500", "GR-8500", "GR-8600", "GR-9500"];
  } else {
    panelTypes = ["BSR-1004", "BS-1638", "GR-6500", "GR-7500", "GR-8500", "GR-8600", "GR-9500", "Phos"];
  }

  const handlePhosUsernameChange = e => {
    setPhosUsername(e.target.value);
  };

  const handlePhosPasswordChange = e => {
    setPhosPassword(e.target.value);
  };

  const handlePanelSelection = type => {
    setSelectedPanelType(type);
    setShowPanelTypeDropdown(false);
  };

  const handleToggleDropdown = () => {
    setShowPanelTypeDropdown(!showPanelTypeDropdown);
  };

  const handleHttpSelection = type => {
    setSelectedHttpType(type);
    setShowHttpTypeDropdown(false);
  };

  const handleToggleDropdownHttp = () => {
    setShowHttpTypeDropdown(!showHttpTypeDropdown);
  };

  const handleDeviceClick = async device => {
    try {
      const hostHTML = await getHostHTMLContent(device.reverseProxyUrl);
      setHostDetails({ name: device.name, url: device.url, html: hostHTML });

      if (!isMobile) {
        setIsToggleButtonHidden(true);
        toggleNavbarCollapse();
      } else {
        setIsToggleButtonHidden(false);
      }
    } catch (error) {
      console.error("Error fetching host's HTML content:", error);
    }
  };

  const handleClosePreview = () => {
    setHostDetails({ name: "", url: "", html: "" });
    // setIsToggleButtonHidden(false);
    if (isMobile) {
      setIsToggleButtonHidden(false);
      return;
    }
    setIsToggleButtonHidden(false);
    toggleNavbarCollapse();
  };

  const handleSelectedGatewayClick = () => {
    setShowSelectedGateway(true);
  };

  const handleCancelClick = () => {
    setShowDeviceForm(!showDeviceForm);
    setNewDevice({ name: "", url: "" });
    setPhosPassword("");
    setPhosUsername("");
    setSelectedPanelType("BSR-1004");
    setSelectedHttpType("http://");
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setNewDevice({ ...newDevice, [name]: value });
  };

  const handleAddDevice = async () => {
    if (newDevice.name && newDevice.url && selectedGateway) {
      let properUrl = `${selectedHttpType}${newDevice.url}`;

      if (properUrl.endsWith("/")) {
        properUrl = properUrl.replace(/\/+$/, "");
      }

      const ipPattern = /^(http|https):\/\/(\d{1,3}\.){3}\d{1,3}(:\d+)?$/;

      if (!ipPattern.test(properUrl)) {
        notify("error", translation("_invalidAddress"));
        return;
      }

      // nginx location
      let reverseProxyLocation;
      if (selectedPanelType?.toLowerCase() === "phos") {
        reverseProxyLocation = `phos${Array(4)
          .fill(0)
          .map(() => String.fromCharCode(97 + Math.floor(Math.random() * 26)))
          .join("")}`;
      } else {
        const parts = selectedPanelType.split("-");
        reverseProxyLocation = `${parts[1]}${Array(4)
          .fill(0)
          .map(() => String.fromCharCode(97 + Math.floor(Math.random() * 26)))
          .join("")}`;
      }

      let reverseProxyUrl = "";

      switch (selectedPanelType) {
        case "BSR-1004":
          reverseProxyUrl = `Proxy/Execute1004?vpnAddress=https://${selectedGateway.vpnAddress}&location=${reverseProxyLocation}`;
          break;
        case "BS-1638":
          reverseProxyUrl = `Proxy/Execute1638?vpnAddress=https://${selectedGateway.vpnAddress}&location=${reverseProxyLocation}`;
          break;
        case "GR-6500":
          reverseProxyUrl = `Proxy/Execute6500?vpnAddress=https://${selectedGateway.vpnAddress}&location=${reverseProxyLocation}`;
          break;
        case "GR-7500":
          reverseProxyUrl = `Proxy/Execute7500?vpnAddress=${selectedGateway.vpnAddress}&loc=${reverseProxyLocation}`;
          break;
        case "GR-8500":
          reverseProxyUrl = `Proxy/Execute8500?vpnAddress=https://${selectedGateway.vpnAddress}&location=${reverseProxyLocation}`;
          break;
        case "GR-8600":
          reverseProxyUrl = `Proxy/Execute8600?vpnAddress=https://${selectedGateway.vpnAddress}&location=${reverseProxyLocation}`;
          break;
        case "GR-9500":
          reverseProxyUrl = `Proxy/Execute9500?vpnAddress=https://${selectedGateway.vpnAddress}&location=${reverseProxyLocation}`;
          break;
        case "Phos":
          reverseProxyUrl = `Proxy/ExecutePhos?vpnAddress=${selectedGateway.vpnAddress}&loc=${reverseProxyLocation}`;
          break;
        default:
          break;
      }

      try {
        setIsProxyReloading(true);
        const addedDevice = await addNewHost(
          newDevice.name,
          reverseProxyUrl,
          properUrl,
          reverseProxyLocation,
          selectedGateway.id,
          selectedPanelType,
          phosUsername,
          phosPassword,
        );
        const deviceToStore = {
          gatewayId: addedDevice.gatewayId,
          reverseProxyUrl: addedDevice.reverseProxyUrl,
          id: addedDevice.id,
          isOwner: selectedGateway.isOwner,
          name: addedDevice.name,
          url: addedDevice.url,
          panelType: addedDevice.panelType,
        };
        dispatch(addHost(deviceToStore));
        notify("success", translation("_addDeviceSuccess"));
        setShowDeviceForm(false);

        setTimeout(() => setIsProxyReloading(false), 8000);
      } catch (error) {
        const errorMessage = error.message?.trim().replace(/^"+|"+$/g, "");
        notify("error", translation(errorMessage));
        // notify("error", translation("_addDeviceError"));
        setIsProxyReloading(false);
      }
    } else {
      notify("warn", translation("_fillFieldsAlert"));
    }
  };

  const handleDeleteDevice = async index => {
    const deviceToDelete = devices[index];
    try {
      setIsProxyReloading(true);
      const response = await deleteHostById(deviceToDelete.id, selectedGateway.id);
      dispatch(deleteHost(deviceToDelete.id));
      const successMessage = response.trim().replace(/^"+|"+$/g, "");
      notify("success", translation(successMessage));
      // notify("success", translation("_successfulPanelRemoval"));

      setTimeout(() => setIsProxyReloading(false), 8000);
    } catch (error) {
      const errorMessage = error.message?.trim().replace(/^"+|"+$/g, "");
      notify("error", translation(errorMessage));
      // notify("error", translation("_deleteDeviceError"));
      setIsProxyReloading(false);
    }
  };

  const handleTryAgainPhos = async device => {
    try {
      setIsProxyReloading(true);
      const response = await tryAgainHostById(device.id, device.gatewayId);

      if (response.ok) {
        dispatch(updateHost({ id: device.id, updates: { hostStatus: 2 } }));
        notify("success", translation("_monitoringReactivated"));
      } else {
        notify("error", translation("_monitoringReactivateFailed"));
      }
    } catch (error) {
      notify("error", translation("_monitoringReactivateFailed"));
    } finally {
      setIsProxyReloading(false);
    }
  };

  const handleEditClick = index => {
    setEditIndex(index);
    setTemporaryName(devices[index].name);
    if (devices[index].panelType === "Phos" || devices[index].panelType === "phos") {
      setUsernamePhosCard(devices[index].username || "");
      setPasswordPhosCard("");
    }
  };

  const handleCancelEdit = index => {
    setTemporaryName(devices[index].name);
    if (devices[index].panelType === "Phos" || devices[index].panelType === "phos") {
      setUsernamePhosCard(devices[index].username);
      setPasswordPhosCard("");
    }
    setEditIndex(null);
  };

  const handleSave = async index => {
    const device = devices[index];
    try {
      await updateHostById(device.id, selectedGateway.id, temporaryName, usernamePhosCard, passwordPhosCard);

      dispatch(
        updateHost({
          id: device.id,
          updates: {
            name: temporaryName,
            username: usernamePhosCard,
          },
        }),
      );

      notify("success", translation("_editDeviceSuccess"));
    } catch (error) {
      const errorMessage = error.message?.trim().replace(/^"+|"+$/g, "");
      notify("error", translation(errorMessage));
    } finally {
      setEditIndex(null);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Enter") {
      handleSave(index);
    }
  };

  const fetchHostStatuses = useCallback(
    async iteration => {
      const isInitialLoad = iteration === "initialLoad";

      if (isInitialLoad) {
        setIsLoading(true);
      }

      try {
        const fetchedHosts = await getAllHosts(selectedGateway.id, selectedGateway.isOwner);
        const hostStatuses = await gethostStatuses(selectedGateway.id);
        const statusMap = hostStatuses.reduce((acc, status) => {
          acc[status.locationName] = status.hostStatus;
          return acc;
        }, {});

        const updatedHosts = fetchedHosts?.map(device => {
          const matchedStatus = statusMap[device.locationName] || 0;

          return {
            ...device,
            hostStatus: matchedStatus,
          };
        });
        dispatch(initializeHost(updatedHosts));
      } catch (error) {
        console.error("Error fetching hosts or statuses:", error);
      } finally {
        if (isInitialLoad) {
          setIsLoading(false);
        }
      }
    },
    [selectedGateway, dispatch],
  );

  useEffect(() => {
    fetchHostStatuses("initialLoad");
    const pollingInterval = parseInt(window.env.HOSTS_POLL_TIME, 10);

    const intervalId = setInterval(() => {
      fetchHostStatuses("polling");
    }, pollingInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchHostStatuses]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (panelDropdownRef.current && !panelDropdownRef.current.contains(event.target)) {
        setShowPanelTypeDropdown(false);
      }
    };

    if (showPanelTypeDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPanelTypeDropdown]);

  useEffect(() => {
    if (selectedPanelType?.toLowerCase() === "phos") {
      setSelectedHttpType("https://");
    }
  }, [selectedPanelType]);

  return hostDetails.url ? (
    <HostPreview isMobile={isMobile} html={hostDetails.html} name={hostDetails.name} onClose={handleClosePreview} />
  ) : (
    <>
      <div className={`flex h-full flex-col space-y-4 overflow-auto bg-backgroundPrimary p-10`}>
        {isProxyReloading && <LoadingSpinner />}

        <div className={`flex items-center text-lg text-textPrimary`}>
          <h2 className="text-3xl font-bold">{translation("_devices")}</h2>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            className={`flex h-[40px] w-[200px] items-center justify-center rounded bg-buttonSubmit p-1 text-buttonText hover:bg-buttonSubmitHover ${
              selectedGateway.isOwner ? "flex" : "hidden"
            }`}
            onClick={handleSelectedGatewayClick}
          >
            {translation("_showSelectedGateway")}
          </button>
          {showDeviceForm ? (
            <div className="flex w-full flex-wrap items-center gap-2 overflow-visible">
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                <input
                  className={`h-[40px] w-[160px] rounded border border-inputBorder bg-inputBackground p-2 text-textPrimary hover:bg-inputHover focus:outline-none`}
                  type="text"
                  name="name"
                  placeholder={translation("_deviceName")}
                  value={newDevice.name}
                  onChange={handleInputChange}
                />
                <div ref={panelDropdownRef} className="relative">
                  <div
                    className={`flex h-[40px] w-[120px] items-center rounded border border-inputBorder bg-inputBackground p-2 text-textPrimary hover:bg-inputHover focus:outline-none`}
                  >
                    <button onClick={handleToggleDropdownHttp} className="flex w-full items-center justify-between p-2">
                      <span>{selectedHttpType}</span>
                      <FontAwesomeIcon icon={showHttpTypeDropdown ? faChevronDown : faChevronRight} size="lg" />
                    </button>
                  </div>
                  {showHttpTypeDropdown && (
                    <div
                      className={`absolute z-[9999] flex max-h-[350px] w-full flex-col overflow-auto rounded border bg-inputBackground text-textPrimary shadow-lg`}
                    >
                      {httpTypes.map((type, index) => (
                        <button
                          key={index}
                          onClick={() => handleHttpSelection(type)}
                          className={`select-none p-2 text-left hover:bg-inputHover`}
                        >
                          {type === selectedHttpType ? <strong>{type}</strong> : type}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <input
                  className={`h-[40px] w-[250px] rounded border border-inputBorder bg-inputBackground p-2 text-textPrimary hover:bg-inputHover focus:outline-none`}
                  type="text"
                  name="url"
                  placeholder={translation("_urlPlaceholder")}
                  value={newDevice.url}
                  onChange={handleInputChange}
                />
                <div ref={panelDropdownRef} className="relative">
                  <div
                    className={`flex h-[40px] w-[140px] items-center rounded border border-inputBorder bg-inputBackground p-2 text-textPrimary hover:bg-inputHover focus:outline-none`}
                  >
                    <button onClick={handleToggleDropdown} className="flex w-full items-center justify-between p-2">
                      <span>{selectedPanelType}</span>
                      <FontAwesomeIcon icon={showPanelTypeDropdown ? faChevronDown : faChevronRight} size="lg" />
                    </button>
                  </div>

                  {showPanelTypeDropdown && (
                    <div
                      className={`absolute z-[9999] flex max-h-[350px] w-full flex-col overflow-auto rounded border bg-inputBackground text-textPrimary shadow-lg`}
                    >
                      {panelTypes.map((type, index) => (
                        <button
                          key={index}
                          onClick={() => handlePanelSelection(type)}
                          className={`select-none p-2 text-left hover:bg-inputHover`}
                        >
                          {type === selectedPanelType ? <strong>{type}</strong> : type}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {selectedPanelType?.toLowerCase() === "phos" && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      type="text"
                      name="username"
                      placeholder={translation("_usernamePlaceholder")}
                      value={phosUsername}
                      onChange={handlePhosUsernameChange}
                      className="h-[40px] w-[160px] rounded border border-inputBorder bg-inputBackground p-2 text-textPrimary"
                    />
                    <div className="relative h-[40px] w-[160px]">
                      <input
                        type={showPasswordAddPhosDevice ? "text" : "password"}
                        name="password"
                        placeholder={translation("_passwordPlaceholder")}
                        value={phosPassword}
                        onChange={handlePhosPasswordChange}
                        className="hide-edge-eye h-full w-full rounded border border-inputBorder bg-inputBackground p-2 pr-10 text-textPrimary"
                      />
                      <span
                        className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
                        onClick={() => setShowPasswordAddPhosDevice(prev => !prev)}
                      >
                        <FontAwesomeIcon icon={showPasswordAddPhosDevice ? faEyeSlash : faEye} />
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-center gap-2">
                <button
                  className={`flex h-[40px] w-[140px] items-center justify-center rounded bg-buttonSubmit p-1 text-buttonText hover:bg-buttonSubmitHover`}
                  onClick={handleAddDevice}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  {translation("_addDevice")}
                </button>
                <button
                  className={`flex h-[40px] w-[140px] items-center justify-center rounded bg-buttonCancel p-1 text-buttonText hover:bg-buttonCancelHover`}
                  onClick={handleCancelClick}
                >
                  {translation("_cancel")}
                </button>
              </div>
            </div>
          ) : (
            <button
              className={`flex h-[40px] w-[160px] items-center justify-center rounded bg-buttonSubmit p-1 text-buttonText hover:bg-buttonSubmitHover ${
                selectedGateway.isOwner ? "flex" : "hidden"
              }`}
              onClick={handleCancelClick}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {translation("_addDevice")}
            </button>
          )}
        </div>

        <div className="h-[90%] flex-grow">
          {isLoading ? (
            <LoadingSpinner />
          ) : devices && devices.length > 0 ? (
            <div className="flex h-full flex-wrap gap-6">
              {devices.map((device, index) => (
                <div
                  key={index}
                  className={`relative flex h-[420px] flex-col items-center overflow-hidden rounded-lg border bg-inputBackground text-textPrimary shadow-lg lg:w-[48%] xl:w-[40%] 2xl:w-[30%]`}
                >
                  {/* Overlay for blocked devices */}
                  {device.hostStatus === 3 && device.panelType?.toLowerCase() === "phos" && editIndex !== index && (
                    <div className="pointer-events-auto absolute inset-0 z-20 flex flex-col items-center justify-center bg-black bg-opacity-60">
                      <div className="relative flex items-center space-x-2">
                        <span className="text-xl font-bold text-white">{translation("_monitoringInactive")}</span>
                        {/* Info icon with hover tooltip */}
                        <div className="group relative">
                          <FontAwesomeIcon icon={faCircleInfo} className="cursor-pointer text-white" />
                          <div className="max-h-70 absolute bottom-full left-1/2 z-50 mb-2 inline-block hidden w-60 -translate-x-3/4 overflow-auto whitespace-normal rounded bg-gray-800 px-4 py-2 text-center text-sm text-white shadow-lg group-hover:block">
                            {translation("_monitoringInfoTooltip")}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleTryAgainPhos(device)}
                        className="mt-4 rounded bg-buttonSubmit px-4 py-2 text-buttonText hover:bg-buttonSubmitHover"
                      >
                        {translation("_tryAgain")}
                      </button>
                    </div>
                  )}

                  {/* Content Section */}
                  <div className="flex h-[80%] w-full flex-col space-y-4 p-6">
                    <div
                      onClick={() => handleDeviceClick(device)}
                      className={`flex cursor-pointer items-center justify-between rounded-lg border border-inputBorder p-4 shadow-md hover:bg-inputHover`}
                    >
                      <span className={`text-lg font-bold text-textPrimary`}>{translation("_connect")}</span>
                      <FontAwesomeIcon icon={faRightToBracket} className="h-[35px] w-[35px]" />
                    </div>

                    {/* Status LED */}
                    <div className={`flex w-full justify-end`}>
                      <div
                        className={`h-4 w-4 rounded-full shadow-lg ${
                          device.hostStatus === 0
                            ? "bg-neutralStatus"
                            : device.hostStatus === 1
                              ? "animate-pulse bg-successStatus"
                              : device.hostStatus === 2
                                ? "animate-pulse bg-errorStatus"
                                : "bg-neutralStatus"
                        }`}
                      ></div>
                    </div>
                    <div className="flex flex-col gap-2 text-base">
                      <div className="flex font-bold underline">{translation("_details")}</div>
                      <div className="flex items-center space-x-2">
                        <strong className={`text-base font-bold text-textPrimary`}>
                          {translation("_deviceName")}:
                        </strong>
                        <input
                          className={`rounded bg-inputBackground text-base text-textPrimary transition duration-300 ease-in-out focus:outline-none ${
                            editIndex === index
                              ? "border border-buttonCancel transition-all duration-200 focus:border-buttonCancelHover"
                              : "border border-inputBackground"
                          }`}
                          type="text"
                          value={editIndex === index ? temporaryName : device.name}
                          disabled={editIndex !== index}
                          onChange={e => setTemporaryName(e.target.value)}
                          onKeyDown={e => handleKeyDown(e, index)}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <strong className={`text-base font-bold text-textPrimary`}>{translation("_panelType")}:</strong>
                        <span className="text-textPrimary">{device.panelType}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <strong className={`text-base font-bold text-textPrimary`}>{translation("_deviceURL")}:</strong>
                        <span className="text-textPrimary">{device.url}</span>
                      </div>
                      {device.panelType?.toLowerCase() === "phos" && (
                        <div className="flex flex-col space-y-1">
                          {/* Username */}
                          <div className="flex items-center space-x-2">
                            <strong className={`text-base font-bold text-textPrimary`}>
                              {translation("_usernamePlaceholder")}:
                            </strong>
                            <input
                              className={`rounded bg-inputBackground text-base text-textPrimary transition duration-300 ease-in-out focus:outline-none ${
                                editIndex === index
                                  ? "border border-buttonCancel transition-all duration-200 focus:border-buttonCancelHover"
                                  : "border border-inputBackground"
                              }`}
                              type="text"
                              value={editIndex === index ? usernamePhosCard : device.username}
                              disabled={editIndex !== index}
                              onChange={e => setUsernamePhosCard(e.target.value)}
                              onKeyDown={e => handleKeyDown(e, index)}
                            />
                          </div>
                          {/* Password */}
                          <div className="flex items-center space-x-2">
                            <strong className={`text-base font-bold text-textPrimary`}>
                              {translation("_passwordPlaceholder")}:
                            </strong>
                            <div className="relative w-full">
                              <input
                                type={showPasswordMap[index] ? "text" : "password"}
                                className={`hide-edge-eye w-full rounded bg-inputBackground pr-10 text-base text-textPrimary transition duration-300 ease-in-out focus:outline-none ${
                                  editIndex === index
                                    ? "border border-buttonCancel transition-all duration-200 focus:border-buttonCancelHover"
                                    : "border border-inputBackground"
                                }`}
                                value={editIndex === index ? passwordPhosCard || "" : "********"}
                                disabled={editIndex !== index}
                                onChange={e => setPasswordPhosCard(e.target.value)}
                                onKeyDown={e => handleKeyDown(e, index)}
                              />

                              {editIndex === index && (
                                <span
                                  className="absolute right-2 top-1/2 -translate-y-1/2 transform cursor-pointer text-gray-500"
                                  onClick={() => setShowPasswordMap(prev => ({ ...prev, [index]: !prev[index] }))}
                                >
                                  <FontAwesomeIcon icon={showPasswordMap[index] ? faEyeSlash : faEye} />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Button Section */}
                  <div
                    className={`absolute bottom-0 left-0 z-30 flex h-[20%] w-full justify-end gap-4 p-4 ${
                      device.isOwner === true ? "flex" : "hidden"
                    }`}
                  >
                    {editIndex === index ? (
                      <>
                        <button
                          className={`flex items-center justify-center rounded bg-buttonSubmit px-2 py-1 text-buttonText hover:bg-buttonSubmitHover`}
                          onClick={() => handleSave(index)}
                        >
                          <FontAwesomeIcon icon={faSave} className="mr-2" />
                          {translation("_save")}
                        </button>
                        <button
                          className={`flex items-center justify-center rounded bg-buttonCancel px-2 py-1 text-buttonText hover:bg-buttonCancelHover`}
                          onClick={() => handleCancelEdit(index)}
                        >
                          {translation("_cancel")}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className={`flex h-8 items-center justify-center rounded bg-buttonSubmit p-2 text-buttonText hover:bg-buttonSubmitHover`}
                          onClick={() => handleEditClick(index)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className={`flex h-8 items-center justify-center rounded bg-buttonCancel p-2 text-buttonText hover:bg-buttonCancelHover`}
                          onClick={() => handleDeleteDevice(index)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <NoData title={translation("_noDevicesFound")} message={translation("_noDevicesFoundDesc")} />
          )}
        </div>
      </div>

      {showSelectedGateway && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-gray-800 bg-opacity-50">
          <div
            className={`relative w-[600px] rounded bg-backgroundPrimary p-6 text-center text-lg text-textPrimary shadow-lg`}
          >
            <button
              className={`absolute right-1 top-0 p-2 text-xl text-buttonCancel hover:text-buttonCancelHover`}
              onClick={() => setShowSelectedGateway(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2 className="mb-6 text-2xl font-semibold">{translation("_showSelectedGateway")}</h2>
            <Gateway key={0} gateway={selectedGateway} index={0} type={"hosts"} />
          </div>
        </div>
      )}
    </>
  );
};

export default Hosts;
